import React, { ChangeEvent, FormEvent, FunctionComponent, useEffect, useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { connect } from 'react-redux';
import { login, loginGoogleAuth } from '../../actions/authActions';
import { clearErrors } from '../../actions/errorActions';
import { AuthState, ErrorState, LOGIN_FAIL } from '../../reducers/types';
import { AppState } from '../../store';
import { useNavigate } from 'react-router-dom';
import GenericIcons from '../generics/GenericIcons';
import authImg from '../../img/vdr-red-auth.jpg';
import '../../css/login.css';

const clientId = '36950189098-epk7rnsviju6q9g16reoikeuvuk33to8.apps.googleusercontent.com';

const LoginForm: FunctionComponent<{
  userInfo: { userUsername: string; userPassword: string };
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onSuccessGoogleAuth: (credentialResponse: CredentialResponse) => void;
  msg: string | null;
}> = ({ userInfo, onChange, onSubmit, onSuccessGoogleAuth, msg }) => {
  const navigate = useNavigate();
  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const handleViewPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setViewPassword(!viewPassword);
  };
  return (
    <form onSubmit={onSubmit} className='login-form-container'>
      <div className='login-form-sub-container'>
        <div className='vdr-auth-heading'>
          <h2 className='login-form-welcome-to-title'>Bienvenidos a</h2>
          <h2 className='login-form-vision-derred-title'>
            Visión<span className='vdr-auth-accent'>DERRED</span>
          </h2>
        </div>

        <GoogleOAuthProvider clientId={clientId!}>
          <GoogleLogin onSuccess={onSuccessGoogleAuth} />
        </GoogleOAuthProvider>

        <div className='login-form-divide-line'>
          <p className='login-form-divide-line-text'>o</p>
        </div>

        <div className='login-form-input-container'>
          <div className='form-group'>
            <label className='form-group-label form-group-label-login'>Nombre de usuario</label>
            <input
              value={userInfo.userUsername}
              className='form-group-input form-group-input-login'
              onChange={onChange}
              type='text'
              placeholder='Nombre de usuario'
              id='userUsername'
              name='userUsername'
              required
              autoFocus
            />
          </div>

          <div className='form-group'>
            <label className='form-group-label form-group-label-login'>Contraseña</label>
            <div className='login-form-password-input'>
              <input
                value={userInfo.userPassword}
                className='form-group-input form-group-input-login'
                onChange={onChange}
                type={viewPassword ? 'text' : 'password'}
                placeholder='*********'
                id='userPassword'
                name='userPassword'
                autoComplete='current-password'
                required
              />
              <button
                type='button'
                className='toggle-icon'
                aria-label={viewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                onClick={handleViewPassword}>
                <GenericIcons icon={viewPassword ? 'visibilityOffIcon' : 'visibilityIcon'} />
              </button>
            </div>
          </div>

          {msg && (
            <div className='login-form-message-container'>
              <GenericIcons icon='errorIcon' imageStyle={{ height: '100%' }} />
              {msg}
            </div>
          )}

          <div className='login-form-divider-line'></div>

          <button
            type='button'
            className='login-form-text-action'
            onClick={() => navigate('/public/sign-up')}>
            Crear cuenta
          </button>

          <div className='login-form-submit-button-container'>
            <button type='submit' className='primary-button-style login-form-submit-button'>
              Iniciar sesión
              <span className='vdr-auth-arrow' aria-hidden='true'>
                →
              </span>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

type Props = {
  auth: AuthState;
  error: ErrorState;
  login: ({
    userUsername,
    userPassword
  }: {
    userUsername: string;
    userPassword: string;
  }) => Promise<void>;
  loginGoogleAuth: (token: string) => Promise<void>;
  clearErrors: typeof clearErrors;
};

const Login: FunctionComponent<Props> = ({ auth, error, login, loginGoogleAuth, clearErrors }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth, navigate]);

  const [userInfo, setUserInfo] = useState<{ userUsername: string; userPassword: string }>({
    userUsername: '',
    userPassword: ''
  });
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (error) {
      // Check for register error
      if (error.id === LOGIN_FAIL) {
        setMsg(error.msg.details);
      } else {
        setMsg(null);
      }
    }
  }, [error]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.name === 'userUsername' ? e.target.value.toLowerCase() : e.target.value;
    setUserInfo({ ...userInfo, [e.target.name]: value });
    clearErrors();
  };

  const handleOnSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    login(userInfo);
  };

  const onSuccessGoogleAuth = async (credentialResponse: CredentialResponse) => {
    loginGoogleAuth(credentialResponse.credential!);
  };

  return (
    <div className='login-container'>
      <div className='login-img-container vdr-auth-aside'>
        <img className='vdr-auth-aside-img' src={authImg} alt='' />
        <span className='vdr-caption vdr-auth-cap' aria-hidden='true'>
          Red de distribución · oriente de Santiago
        </span>
      </div>

      <LoginForm
        onSubmit={handleOnSubmit}
        userInfo={userInfo}
        onChange={handleOnChange}
        onSuccessGoogleAuth={onSuccessGoogleAuth}
        msg={msg}
      />
    </div>
  );
};

export default connect(({ auth, error }: AppState) => ({ auth, error }), {
  login,
  loginGoogleAuth,
  clearErrors
})(Login);
